import React, { useState, useEffect, useReducer } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Popover from "@material-ui/core/Popover";
import Badge from "@material-ui/core/Badge";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Divider from "@material-ui/core/Divider";
import ListItemText from "@material-ui/core/ListItemText";

import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import AddIcon from "@material-ui/icons/Add";
import TuneIcon from "@material-ui/icons/Tune";
import ViewColumnOutlinedIcon from "@material-ui/icons/ViewColumnOutlined";

import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import ClientModal from "../../components/ClientModal";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import toastError from "../../errors/toastError";
import {
  displayClientName,
  displayClientSubtitle,
} from "../../utils/clientName";
import Box from "@material-ui/core/Box";
import PublishIcon from "@material-ui/icons/Publish";
import ClientImportModal from "../../components/ClientImportModal";

const reducer = (state, action) => {
  if (action.type === "LOAD_CLIENTS") {
    const clients = action.payload;
    const newClients = [];
    clients.forEach((client) => {
      const idx = state.findIndex((c) => c.id === client.id);
      if (idx !== -1) state[idx] = client;
      else newClients.push(client);
    });
    return [...state, ...newClients];
  }
  if (action.type === "UPDATE_CLIENT") {
    const client = action.payload;
    const idx = state.findIndex((c) => c.id === client.id);
    if (idx !== -1) {
      state[idx] = client;
      return [...state];
    }
    return [client, ...state];
  }
  if (action.type === "DELETE_CLIENT") {
    return state.filter((c) => c.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

const useStyles = makeStyles((theme) => ({
  search: { minWidth: 300 },
  typeFilter: { minWidth: 150 },
  nameCell: { display: "flex", flexDirection: "column", gap: 1 },
  nameSubtitle: { color: theme.palette.text.secondary },
  tagList: { display: "flex", flexWrap: "wrap", gap: 4 },
  muted: { color: theme.palette.text.hint },
  moreFiltersPaper: {
    padding: theme.spacing(2),
    width: 320,
    maxHeight: "70vh",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  moreFiltersEmpty: {
    color: theme.palette.text.hint,
    fontSize: 13,
  },
}));

// A escolha de colunas é por navegador, não por usuário no servidor — é
// preferência de tela, do mesmo jeito que o tema claro/escuro e o filtro de
// grupos do WhatsApp já são guardados aqui.
const COLUNAS_STORAGE_KEY = "clients.visibleColumns";

// Colunas que sempre aparecem: sem o nome não dá para identificar a linha, e
// sem ações não dá para agir nela. Não entram no seletor de colunas.
const COLUNAS_FIXAS = ["name", "actions"];

const personTypeLabel = (t) => (t === "PJ" ? "PJ" : "PF");

const displayName = (c) => displayClientName(c, "(Sem nome)");

const displayDoc = (c) => (c.personType === "PJ" ? c.cnpj : c.cpf);

const Clients = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [personType, setPersonType] = useState("");
  const [statusId, setStatusId] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [tagIds, setTagIds] = useState([]);
  // Chave é o id da definição (string, porque vem de um <select>/<input>
  // controlado); valor vazio nunca é enviado — ver `activeCustomFilters`.
  const [customFieldFilters, setCustomFieldFilters] = useState({});
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [extraInfoNames, setExtraInfoNames] = useState([]);
  const [moreFiltersAnchor, setMoreFiltersAnchor] = useState(null);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const salvo = localStorage.getItem(COLUNAS_STORAGE_KEY);
      return salvo ? JSON.parse(salvo) : null;
    } catch {
      return null;
    }
  });
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [tags, setTags] = useState([]);
  const [clients, dispatch] = useReducer(reducer, []);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const openCreate = () => { setEditingId(null); setModalOpen(true); };
  const openEdit = (id) => { setEditingId(id); setModalOpen(true); };
  const handleSaved = (saved) => {
    dispatch({ type: "UPDATE_CLIENT", payload: saved });
  };

  // Só o que tem valor de fato conta como filtro ativo — um campo aberto no
  // popover e deixado em branco não deve entrar na busca nem no contador do
  // botão "Mais filtros".
  const activeCustomFilters = Object.entries(customFieldFilters).filter(
    ([, v]) => v !== "" && v !== undefined && v !== null
  );

  useEffect(() => {
    (async () => {
      try {
        const [st, src, tg, fd, ein] = await Promise.all([
          api.get("/client-statuses"),
          api.get("/client-sources"),
          api.get("/client-tags"),
          api.get("/client-field-definitions"),
          api.get("/client-extra-info-names"),
        ]);
        setStatuses(st.data);
        setSources(src.data);
        setTags(tg.data);
        setFieldDefinitions((fd.data || []).filter((f) => f.active));
        setExtraInfoNames(ein.data || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, personType, statusId, sourceId, JSON.stringify(tagIds), JSON.stringify(customFieldFilters)]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const customParams = activeCustomFilters.reduce((acc, [defId, value]) => {
          acc[`cf_${defId}`] = value;
          return acc;
        }, {});
        const { data } = await api.get("/clients", {
          params: {
            searchParam,
            pageNumber,
            personType: personType || undefined,
            statusId: statusId || undefined,
            sourceId: sourceId || undefined,
            tagIds: tagIds.length ? tagIds : undefined,
            ...customParams,
          },
        });
        dispatch({ type: "LOAD_CLIENTS", payload: data.clients });
        setHasMore(data.hasMore);
        setCount(data.count);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber, personType, statusId, sourceId, JSON.stringify(tagIds), JSON.stringify(customFieldFilters)]);

  const hasActiveFilters =
    !!searchParam || !!personType || !!statusId || !!sourceId || tagIds.length > 0 || activeCustomFilters.length > 0;

  const clearCustomFilter = (definitionId) => {
    setCustomFieldFilters((prev) => {
      const next = { ...prev };
      delete next[definitionId];
      return next;
    });
  };

  const setCustomFilter = (definitionId, value) => {
    setCustomFieldFilters((prev) => ({ ...prev, [definitionId]: value }));
  };

  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());

  const loadMore = () => setPageNumber((p) => p + 1);
  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente removido");
      dispatch({ type: "DELETE_CLIENT", payload: id });
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
    setConfirmOpen(false);
  };

  const dash = <span className={classes.muted}>—</span>;

  const columns = [
    {
      key: "name",
      label: "Nome",
      render: (client) => {
        const subtitle = displayClientSubtitle(client);
        return (
          <div className={classes.nameCell}>
            <Typography variant="subtitle2">{displayName(client)}</Typography>
            {subtitle && (
              <Typography variant="caption" className={classes.nameSubtitle}>
                {subtitle}
              </Typography>
            )}
          </div>
        );
      },
    },
    {
      key: "personType",
      label: "Tipo",
      align: "center",
      width: 72,
      render: (client) => personTypeLabel(client.personType),
    },
    {
      key: "doc",
      label: "CPF/CNPJ",
      render: (client) => displayDoc(client) || dash,
    },
    { key: "email", label: "E-mail", render: (client) => client.email || dash },
    {
      key: "phone",
      label: "Telefone",
      render: (client) => client.mobilePhone || client.phone || dash,
    },
    {
      key: "status",
      label: "Status",
      align: "center",
      render: (client) =>
        client.clientStatus ? (
          <StatusChip
            dot
            label={client.clientStatus.name}
            color={client.clientStatus.color}
          />
        ) : (
          <StatusChip dot status={client.status} />
        ),
    },
    {
      key: "source",
      label: "Origem",
      render: (client) =>
        client.source ? (
          <StatusChip label={client.source.name} color={client.source.color} />
        ) : (
          dash
        ),
    },
    {
      key: "tags",
      label: "Tags",
      render: (client) => {
        const tags = client.tags || [];
        if (!tags.length) return dash;
        return (
          <div className={classes.tagList}>
            {tags.map((tag) => (
              <StatusChip key={tag.id} label={tag.name} color={tag.color} />
            ))}
          </div>
        );
      },
    },
  ];

  // Uma coluna por definição ativa, lida do mesmo jeito que o filtro:
  // procura em `client.customFieldValues` o valor cujo `definitionId` bate
  // com o desta definição. Boolean vira "Sim"/"Não" — mostrar "true"/"false"
  // cru não é o que a pessoa que só olha a tabela vai entender.
  const customFieldColumns = fieldDefinitions.map((f) => ({
    key: `cf_${f.id}`,
    label: f.label,
    render: (client) => {
      const registro = (client.customFieldValues || []).find(
        (v) => v.definitionId === f.id
      );
      const valor = registro?.value;
      if (valor === undefined || valor === null || valor === "") return dash;
      if (f.type === "boolean") return valor === "true" ? "Sim" : "Não";
      return valor;
    },
  }));

  // Uma coluna por nome já usado em "Informações adicionais" — o campo
  // livre que qualquer um digita na ficha, sem catálogo administrado. Nome
  // é comparado exato: "Convênio" e "convênio" viram duas colunas
  // diferentes de propósito, porque juntar as duas exigiria adivinhar que
  // são a mesma coisa — e adivinhar errado misturaria dado de gente
  // diferente. Quando isso acontecer bastante, é sinal de que vale a pena
  // promover o nome para um campo personalizado de verdade, com catálogo.
  const extraInfoColumns = extraInfoNames.map((ei) => ({
    key: `ei_${ei.name}`,
    label: ei.name,
    render: (client) => {
      const registro = (client.extraInfo || []).find((v) => v.name === ei.name);
      return registro?.value || dash;
    },
  }));

  const actionsColumn = {
    key: "actions",
    label: "Ações",
    type: "actions",
    render: (client) => (
      <>
        <Tooltip title="Ver detalhes">
          <IconButton
            size="small"
            onClick={() => history.push(`/clients/${client.id}`)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => openEdit(client.id)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir">
          <IconButton
            size="small"
            onClick={() => {
              setDeleting(client);
              setConfirmOpen(true);
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </>
    ),
  };

  const allColumns = [...columns, ...customFieldColumns, ...extraInfoColumns, actionsColumn];

  // `visibleColumns === null` é "nunca mexeu" — mostra tudo. A partir do
  // primeiro toggle vira uma lista concreta e passa a decidir sozinha, sem
  // herdar coluna nova que apareça depois (um campo personalizado criado
  // amanhã entra desmarcado, não precisa que a pessoa "descubra" que sumiu
  // algo — ela nunca viu aparecer).
  const visibleColumnSet =
    visibleColumns === null ? null : new Set(visibleColumns);
  const displayedColumns = allColumns.filter(
    (c) => COLUNAS_FIXAS.includes(c.key) || !visibleColumnSet || visibleColumnSet.has(c.key)
  );

  const toggleableColumns = allColumns.filter((c) => !COLUNAS_FIXAS.includes(c.key));

  const isColumnVisible = (key) =>
    !visibleColumnSet ? true : visibleColumnSet.has(key);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      // Primeira interação: parte de "tudo visível" (todas as toggleáveis)
      // e tira só a que foi clicada.
      const base = prev === null ? toggleableColumns.map((c) => c.key) : prev;
      const next = base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
      try {
        localStorage.setItem(COLUNAS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // localStorage indisponível (modo privado, quota): a preferência só
        // não sobrevive a um F5 — não é motivo para quebrar o filtro.
      }
      return next;
    });
  };

  const resetColumns = () => {
    setVisibleColumns(null);
    try {
      localStorage.removeItem(COLUNAS_STORAGE_KEY);
    } catch {
      /* ver comentário acima */
    }
  };

  return (
    <MainContainer>
      <ClientModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        clientId={editingId}
        onSave={handleSaved}
      />
      <ConfirmationModal
        title={deleting ? `Excluir ${displayName(deleting)}?` : ""}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => deleting && handleDelete(deleting.id)}
      >
        Esta ação não pode ser desfeita. Contatos e tickets vinculados serão desassociados.
      </ConfirmationModal>

      <ClientImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          // Recarrega do zero: a importação pode ter criado dezenas de
          // registros que não cabem na página atual.
          setPageNumber(1);
          dispatch({ type: "RESET" });
        }}
      />

      <PageHeader
        title="Clientes"
        count={count}
        subtitle="Base de clientes pessoa física e jurídica do CRM."
        actions={
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PublishIcon />}
              onClick={() => setImportOpen(true)}
            >
              Importar base
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openCreate}
            >
              Novo cliente
            </Button>
          </Box>
        }
        filters={
          <>
            <TextField
              className={classes.search}
              placeholder="Buscar por nome, CPF/CNPJ, e-mail ou telefone"
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Tipo"
              className={classes.typeFilter}
              value={personType}
              onChange={(e) => setPersonType(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="PF">Pessoa física</MenuItem>
              <MenuItem value="PJ">Pessoa jurídica</MenuItem>
            </TextField>
            <TextField
              select
              label="Status"
              className={classes.typeFilter}
              value={statusId}
              onChange={(e) => setStatusId(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {statuses.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Origem"
              className={classes.typeFilter}
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {sources.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Tags"
              className={classes.typeFilter}
              value={tagIds}
              onChange={(e) => setTagIds(e.target.value)}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (!selected.length) return "Todas";
                  const nomes = tags
                    .filter((t) => selected.includes(t.id))
                    .map((t) => t.name);
                  // Cliente com QUALQUER uma das marcadas — igual ao filtro
                  // de tag da lista de atendimentos.
                  return nomes.join(", ");
                },
              }}
            >
              {tags.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <Checkbox size="small" checked={tagIds.includes(t.id)} />
                  <ListItemText primary={t.name} />
                </MenuItem>
              ))}
            </TextField>

            {/* Os campos personalizados são dinâmicos — quantos existirem — e
                por isso não cabem como um <TextField> fixo ao lado dos
                demais: colocar um controle por campo direto na barra faria
                ela crescer sem limite conforme o catálogo cresce. Ficam atrás
                deste botão, um por definição ativa. */}
            {fieldDefinitions.length > 0 && (
              <>
                <Badge
                  color="primary"
                  badgeContent={activeCustomFilters.length}
                  invisible={activeCustomFilters.length === 0}
                >
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<TuneIcon />}
                    onClick={(e) => setMoreFiltersAnchor(e.currentTarget)}
                  >
                    Mais filtros
                  </Button>
                </Badge>
                <Popover
                  open={Boolean(moreFiltersAnchor)}
                  anchorEl={moreFiltersAnchor}
                  onClose={() => setMoreFiltersAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <div className={classes.moreFiltersPaper}>
                    <Typography variant="subtitle2">Campos personalizados</Typography>
                    <Divider />
                    {fieldDefinitions.map((f) => {
                      const valor = customFieldFilters[f.id] ?? "";
                      if (f.type === "boolean") {
                        return (
                          <FormControlLabel
                            key={f.id}
                            control={
                              <Checkbox
                                indeterminate={valor === ""}
                                checked={valor === "true"}
                                onChange={() => {
                                  if (valor === "") return setCustomFilter(f.id, "true");
                                  if (valor === "true") return setCustomFilter(f.id, "false");
                                  return clearCustomFilter(f.id);
                                }}
                              />
                            }
                            label={`${f.label}${valor === "" ? " (qualquer)" : valor === "true" ? " (sim)" : " (não)"}`}
                          />
                        );
                      }
                      if (f.type === "select") {
                        return (
                          <TextField
                            key={f.id}
                            select
                            label={f.label}
                            value={valor}
                            onChange={(e) => setCustomFilter(f.id, e.target.value)}
                            fullWidth
                            margin="dense"
                            variant="outlined"
                          >
                            <MenuItem value="">Todos</MenuItem>
                            {(f.options || []).map((op) => (
                              <MenuItem key={op} value={op}>{op}</MenuItem>
                            ))}
                          </TextField>
                        );
                      }
                      return (
                        <TextField
                          key={f.id}
                          label={f.label}
                          type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
                          value={valor}
                          onChange={(e) => setCustomFilter(f.id, e.target.value)}
                          fullWidth
                          margin="dense"
                          variant="outlined"
                          InputLabelProps={f.type === "date" ? { shrink: true } : undefined}
                        />
                      );
                    })}
                    {activeCustomFilters.length > 0 && (
                      <Button size="small" onClick={() => setCustomFieldFilters({})}>
                        Limpar campos personalizados
                      </Button>
                    )}
                  </div>
                </Popover>
              </>
            )}

            <Button
              variant="outlined"
              size="medium"
              startIcon={<ViewColumnOutlinedIcon />}
              onClick={(e) => setColumnsAnchor(e.currentTarget)}
            >
              Colunas
            </Button>
            <Popover
              open={Boolean(columnsAnchor)}
              anchorEl={columnsAnchor}
              onClose={() => setColumnsAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <div className={classes.moreFiltersPaper}>
                <Typography variant="subtitle2">Colunas exibidas</Typography>
                <Divider />
                {toggleableColumns
                  .filter((c) => !c.key.startsWith("cf_") && !c.key.startsWith("ei_"))
                  .map((c) => (
                    <FormControlLabel
                      key={c.key}
                      control={
                        <Checkbox
                          size="small"
                          checked={isColumnVisible(c.key)}
                          onChange={() => toggleColumn(c.key)}
                        />
                      }
                      label={c.label}
                    />
                  ))}

                {customFieldColumns.length > 0 && (
                  <>
                    <Typography variant="caption" className={classes.moreFiltersEmpty}>
                      Campos personalizados
                    </Typography>
                    {customFieldColumns.map((c) => (
                      <FormControlLabel
                        key={c.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={isColumnVisible(c.key)}
                            onChange={() => toggleColumn(c.key)}
                          />
                        }
                        label={c.label}
                      />
                    ))}
                  </>
                )}

                {extraInfoColumns.length > 0 && (
                  <>
                    <Typography variant="caption" className={classes.moreFiltersEmpty}>
                      Informações adicionais
                    </Typography>
                    {extraInfoColumns.map((c) => (
                      <FormControlLabel
                        key={c.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={isColumnVisible(c.key)}
                            onChange={() => toggleColumn(c.key)}
                          />
                        }
                        label={c.label}
                      />
                    ))}
                  </>
                )}

                {visibleColumns !== null && (
                  <Button size="small" onClick={resetColumns}>
                    Restaurar padrão
                  </Button>
                )}
              </div>
            </Popover>
          </>
        }
      />

      <DataTable
        columns={displayedColumns}
        rows={clients}
        loading={loading}
        onScroll={handleScroll}
        emptyIcon={<PeopleOutlineIcon />}
        emptyTitle={
          hasActiveFilters
            ? "Nenhum cliente encontrado"
            : "Nenhum cliente cadastrado"
        }
        emptyDescription={
          hasActiveFilters
            ? "Ajuste a busca ou os filtros para ver outros resultados."
            : "Cadastre o primeiro cliente para começar a registrar negócios, tickets e cobranças."
        }
        emptyAction={
          !hasActiveFilters ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openCreate}
            >
              Novo cliente
            </Button>
          ) : null
        }
      />
    </MainContainer>
  );
};

export default Clients;
